import { collectOffsetPages } from '../../shared/collectOffsetPages';
import type { ApiClient } from '../../api/client';
import type { PeopleProfilePort } from '../../features/people/public';

type PortResponse<K extends keyof PeopleProfilePort> = Awaited<ReturnType<NonNullable<PeopleProfilePort[K]>>>;
type ItemCollection<T> = { total: number; data: T[] };

async function requestAll<T extends { id: string }>(
  client: ApiClient,
  pathForOffset: (offset: number) => string,
  pageSize = 100,
): Promise<ItemCollection<T>> {
  return collectOffsetPages((offset) => client.requestTenant<ItemCollection<T>>(pathForOffset(offset)), pageSize);
}

function jsonMutation(method: 'POST' | 'PUT', body: unknown, idempotencyKey: string): RequestInit {
  return { method, headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(body) };
}

export function createPeopleProfileHttpAdapter(client: ApiClient): PeopleProfilePort {
  return {
    listPersonHistory(personId, offset) { return client.requestTenant<PortResponse<'listPersonHistory'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/history?limit=50&offset=${offset}`); },
    getWorker(workerId) {
      return client.getWorker(workerId);
    },
    getWorkerRewards(workerId, asOf) {
      const query = asOf ? `?asOf=${encodeURIComponent(asOf)}` : '';
      return client.requestTenant<PortResponse<'getWorkerRewards'>>(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/rewards${query}`);
    },
    getPersonNames(personId) {
      return client.requestTenant<PortResponse<'getPersonNames'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/names`);
    },
    updatePersonNames(personId, input, idempotencyKey) {
      return client.requestTenant<PortResponse<'updatePersonNames'>>(
        `/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/names`,
        jsonMutation('PUT', input, idempotencyKey),
      );
    },
    listPersonHomeEmails(personId) {
      return requestAll(client, (offset) => `/api/person/v4/{tenant}/people/${encodeURIComponent(personId)}/homeEmails?limit=100&offset=${offset}`);
    },
    listPersonHomePhones(personId) {
      return requestAll(client, (offset) => `/api/person/v4/{tenant}/people/${encodeURIComponent(personId)}/homePhones?limit=100&offset=${offset}`);
    },
    listPersonHomeAddresses(personId) {
      return requestAll(client, (offset) => `/api/person/v4/{tenant}/people/${encodeURIComponent(personId)}/homeAddresses?limit=100&offset=${offset}`);
    },
    listPersonEmergencyContacts(personId) {
      return client.requestTenant<PortResponse<'listPersonEmergencyContacts'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/emergencyContacts`);
    },
    listPersonWorkEmails(personId) {
      return requestAll(client, (offset) => `/api/person/v4/{tenant}/people/${encodeURIComponent(personId)}/workEmails?limit=100&offset=${offset}`);
    },
    listPersonWorkPhones(personId) {
      return requestAll(client, (offset) => `/api/person/v4/{tenant}/people/${encodeURIComponent(personId)}/workPhones?limit=100&offset=${offset}`);
    },
    getPersonPersonalInformation(personId) {
      return client.requestTenant<PortResponse<'getPersonPersonalInformation'>>(`/api/person/v4/{tenant}/people/${encodeURIComponent(personId)}/personalInformation`);
    },
    createContactChange(personId, input, idempotencyKey) {
      return client.requestTenant<PortResponse<'createContactChange'>>(
        `/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/contactChanges`,
        jsonMutation('POST', input, idempotencyKey),
      );
    },
    submitContactChange(change, idempotencyKey) {
      const segment = change.scope === 'HOME' ? 'homeContactInformationChanges' : 'workContactInformationChanges';
      return client.requestTenant<PortResponse<'submitContactChange'>>(
        `/api/person/v4/{tenant}/${segment}/${encodeURIComponent(change.id)}/submit`,
        jsonMutation('POST', {}, idempotencyKey),
      );
    },
    uploadProfilePhoto(personId, file) {
      const body = new FormData();
      body.set('file', file);
      return client.requestTenant<PortResponse<'uploadProfilePhoto'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/profilePhoto`, { method: 'PUT', body });
    },
    deleteProfilePhoto(personId) {
      return client.requestTenant<void>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/profilePhoto`, { method: 'DELETE' });
    },
    getMyFamily() {
      return client.requestTenant<PortResponse<'getMyFamily'>>('/api/hr/v1/{tenant}/me/family');
    },
    getMyCareer() {
      return client.requestTenant<PortResponse<'getMyCareer'>>('/api/hr/v1/{tenant}/me/career');
    },
    getPersonFamily(personId) {
      return client.requestTenant<PortResponse<'getPersonFamily'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/family`);
    },
    getPersonCareer(personId) {
      return client.requestTenant<PortResponse<'getPersonCareer'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/career`);
    },
    listMyFamilyChanges() {
      return client.requestTenant<PortResponse<'listMyFamilyChanges'>>('/api/hr/v1/{tenant}/me/familyChanges');
    },
    listMyCareerChanges() {
      return client.requestTenant<PortResponse<'listMyCareerChanges'>>('/api/hr/v1/{tenant}/me/careerChanges');
    },
    listPersonFamilyChanges(personId) {
      return client.requestTenant<PortResponse<'listPersonFamilyChanges'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/familyChanges`);
    },
    listPersonCareerChanges(personId) {
      return client.requestTenant<PortResponse<'listPersonCareerChanges'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/careerChanges`);
    },
    getMyFamilyChange(changeId) {
      return client.requestTenant<PortResponse<'getMyFamilyChange'>>(`/api/hr/v1/{tenant}/me/familyChanges/${encodeURIComponent(changeId)}`);
    },
    getMyCareerChange(changeId) {
      return client.requestTenant<PortResponse<'getMyCareerChange'>>(`/api/hr/v1/{tenant}/me/careerChanges/${encodeURIComponent(changeId)}`);
    },
    getPersonFamilyChange(personId, changeId) {
      return client.requestTenant<PortResponse<'getPersonFamilyChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/familyChanges/${encodeURIComponent(changeId)}`);
    },
    getPersonCareerChange(personId, changeId) {
      return client.requestTenant<PortResponse<'getPersonCareerChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/careerChanges/${encodeURIComponent(changeId)}`);
    },
    createFamilyChange(input, idempotencyKey) {
      return client.requestTenant<PortResponse<'createFamilyChange'>>('/api/hr/v1/{tenant}/me/familyChanges', jsonMutation('POST', input, idempotencyKey));
    },
    createCareerChange(input, idempotencyKey) {
      return client.requestTenant<PortResponse<'createCareerChange'>>('/api/hr/v1/{tenant}/me/careerChanges', jsonMutation('POST', input, idempotencyKey));
    },
    submitFamilyChange(changeId, idempotencyKey) {
      return client.requestTenant<PortResponse<'submitFamilyChange'>>(`/api/hr/v1/{tenant}/me/familyChanges/${encodeURIComponent(changeId)}/submit`, jsonMutation('POST', {}, idempotencyKey));
    },
    submitCareerChange(changeId, idempotencyKey) {
      return client.requestTenant<PortResponse<'submitCareerChange'>>(`/api/hr/v1/{tenant}/me/careerChanges/${encodeURIComponent(changeId)}/submit`, jsonMutation('POST', {}, idempotencyKey));
    },
    createPersonFamilyChange(personId, input, idempotencyKey) {
      return client.requestTenant<PortResponse<'createPersonFamilyChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/familyChanges`, jsonMutation('POST', input, idempotencyKey));
    },
    createPersonCareerChange(personId, input, idempotencyKey) {
      return client.requestTenant<PortResponse<'createPersonCareerChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/careerChanges`, jsonMutation('POST', input, idempotencyKey));
    },
    submitPersonFamilyChange(personId, changeId, idempotencyKey) {
      return client.requestTenant<PortResponse<'submitPersonFamilyChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/familyChanges/${encodeURIComponent(changeId)}/submit`, jsonMutation('POST', {}, idempotencyKey));
    },
    submitPersonCareerChange(personId, changeId, idempotencyKey) {
      return client.requestTenant<PortResponse<'submitPersonCareerChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/careerChanges/${encodeURIComponent(changeId)}/submit`, jsonMutation('POST', {}, idempotencyKey));
    },
    approvePersonFamilyChange(personId, changeId, idempotencyKey) {
      return client.requestTenant<PortResponse<'approvePersonFamilyChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/familyChanges/${encodeURIComponent(changeId)}/approve`, jsonMutation('POST', {}, idempotencyKey));
    },
    approvePersonCareerChange(personId, changeId, idempotencyKey) {
      return client.requestTenant<PortResponse<'approvePersonCareerChange'>>(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/careerChanges/${encodeURIComponent(changeId)}/approve`, jsonMutation('POST', {}, idempotencyKey));
    },
  };
}
